// 入口:FluentProvider(webLightTheme)+ QueryClient + Router + Toaster + auth bootstrap
// 启动时显示全屏 Spinner,等 bootstrap() 完成后再渲染 App,避免登录页闪烁
import { StrictMode, useEffect, Component, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  FluentProvider,
  webLightTheme,
  Toaster,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { useAuthStore } from './store/auth';
import { getAuthMode, refreshAuthMode } from './lib/authMode';
import './styles/global.css';

// 全局兜底:未捕获的同步/异步(含 React 渲染)错误都打印到控制台,用于排查登录页/任意页空白。
window.addEventListener('error', (e) => {
  console.error('[gitman-dbg] uncaught error:', e.message, e.error);
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[gitman-dbg] unhandled promise rejection:', e.reason);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// 诊断:捕获渲染期错误(含 React 无限更新 #185),打印 componentStack 定位具体组件。
class RootErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[gitman-dbg] <RootErrorBoundary/> caught:', error?.name || error, error?.message || '');
    console.error('[gitman-dbg] componentStack:\n' + (info?.componentStack || '(empty)'));
  }
  render() {
    return this.props.children;
  }
}

const useBootStyles = makeStyles({
  splash: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  splashLabel: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
});

function Root() {
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const styles = useBootStyles();

  useEffect(() => {
    const t0 = performance.now();
    console.log(
      '[gitman-dbg] Root mount: bootstrapped=',
      bootstrapped,
      'authMode=',
      getAuthMode(),
      'tokens in localStorage=',
      Object.keys(localStorage).filter((k) => k.includes('gitman')).join(',') || '(none)',
    );
    bootstrap()
      .then(() =>
        console.log(
          `[gitman-dbg] bootstrap() resolved OK in ${Math.round(performance.now() - t0)}ms -> bootstrapped=true, LoginPage should render`,
        ),
      )
      .catch((e) => console.error('[gitman-dbg] bootstrap() REJECTED, splash may stick forever:', e));
    refreshAuthMode()
      .then((m) => console.log('[gitman-dbg] refreshAuthMode() ->', m))
      .catch((e) => console.error('[gitman-dbg] refreshAuthMode() error:', e));
  }, [bootstrap]);

  console.log(
    '[gitman-dbg] Root render: bootstrapped=',
    bootstrapped,
    '->',
    bootstrapped ? 'render <App/>' : 'still splash spinner',
  );

  return (
    <FluentProvider theme={webLightTheme} style={{ height: '100%' }}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {bootstrapped ? (
            <RootErrorBoundary>
              <App />
            </RootErrorBoundary>
          ) : (
            <div className={styles.splash}>
              <Spinner size="large" />
              <span className={styles.splashLabel}>加载中…</span>
            </div>
          )}
        </BrowserRouter>
        {/* Toast 容器 — position="top" 从顶部弹出,不占用文档流,不会导致页面跳动 */}
        <Toaster position="top" />
      </QueryClientProvider>
    </FluentProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
